import React, { useEffect, useState } from "react";
import API, { CONFIG, UPDATE_CONFIG, UPDATE_WORD_BONUS, UPDATE_LETTER_RATES } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import { Button, InputNumber, Input, message } from "antd";
import ModalCommon from "../../components/Modal";

const PHRASE_DEFS = [
  { key: "DOANVIEN", label: "ĐOÀN VIÊN", aliases: ["ĐOÀN VIÊN", "DOANVIEN", "DOAN_VIEN", "DOAN VIEN"], defaultScore: 888 },
  { key: "TRANGRAM", label: "TRĂNG RẰM", aliases: ["TRĂNG RẰM", "TRANGRAM", "TRANG_RAM", "TRANG RAM"], defaultScore: 688 },
  { key: "TRANGVANG", label: "TRĂNG VÀNG", aliases: ["TRĂNG VÀNG", "TRANGVANG", "TRANG_VANG", "TRANG VANG"], defaultScore: 588 },
  { key: "PHUCLOC", label: "PHÚC LỘC", aliases: ["PHÚC LỘC", "PHUCLOC", "PHUC_LOC", "PHUC LOC"], defaultScore: 488 },
  { key: "RUOCDEN", label: "RƯỚC ĐÈN", aliases: ["RƯỚC ĐÈN", "RUOCDEN", "RUOC_DEN", "RUOC DEN"], defaultScore: 388 },
  { key: "PHACO", label: "PHÁ CỖ", aliases: ["PHÁ CỖ", "PHACO", "PHA_CO", "PHA CO"], defaultScore: 288 },
];

const LETTER_DEFS = [
  { key: "A", defaultRate: 12 },
  { key: "N", defaultRate: 12 },
  { key: "O", defaultRate: 10 },
  { key: "R", defaultRate: 9 },
  { key: "C", defaultRate: 8 },
  { key: "G", defaultRate: 7 },
  { key: "H", defaultRate: 6 },
  { key: "U", defaultRate: 6 },
  { key: "D", defaultRate: 5 },
  { key: "E", defaultRate: 5 },
  { key: "T", defaultRate: 5 },
  { key: "P", defaultRate: 4 },
  { key: "V", defaultRate: 3 },
  { key: "I", defaultRate: 3 },
  { key: "L", defaultRate: 3 },
  { key: "M", defaultRate: 2 },
];

const toPercent = (raw, fallback = 0) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return n <= 1 ? Math.round(n * 10000) / 100 : n;
};

const toDecimalRate = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 0;
  return Number((n / 100).toFixed(4));
};

const roundPct = (n) => Math.round((Number(n) || 0) * 100) / 100;

const sumRates = (hit, box) => roundPct(roundPct(hit) + roundPct(box));

const vipRateOverflowError = (hit, box) => {
  const total = sumRates(hit, box);
  if (total > 100) {
    return `${roundPct(hit)}% điểm thưởng + ${roundPct(box)}% hộp chữ ngọc = ${total}%. Tổng đang vượt 100%, vui lòng giảm một trong hai ô.`;
  }
  return null;
};

const vipRateSumHint = (hit, box) => {
  const overflow = vipRateOverflowError(hit, box);
  if (overflow) return overflow;
  const remain = roundPct(100 - sumRates(hit, box));
  if (Math.abs(remain) < 0.01) return null;
  return `Còn lại ${remain}% tỉ lệ không có điểm (100% - ${roundPct(hit)}% điểm thưởng - ${roundPct(box)}% hộp chữ ngọc).`;
};

const sumLetterRates = (rates) =>
  roundPct(LETTER_DEFS.reduce((sum, l) => sum + roundPct(rates?.[l.key]), 0));

const letterRateSumError = (rates) => {
  const total = sumLetterRates(rates);
  if (total > 100) return `Tổng tỷ lệ nhận chữ ${total}% vượt 100%.`;
  if (Math.abs(total - 100) > 0.01) return `Tổng tỷ lệ nhận chữ phải bằng 100% (hiện ${total}%).`;
  return null;
};

const pickFirstObject = (...objs) =>
  objs.find((o) => o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length) || {};

const readAliasedNumber = (src, aliases, fallback) => {
  for (const alias of aliases) {
    if (src?.[alias] != null && Number.isFinite(Number(src[alias]))) return Number(src[alias]);
  }
  return fallback;
};

const tableHeader = { background: "#1890ff", color: "#fff", padding: "10px 12px", textAlign: "left" };
const tableCell = { padding: "8px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff" };

const SetupScore = () => {
  const [configGame, setConfigGame] = useState();
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);

  const [vipKeys, setVipKeys] = useState([]); // ["NEW","NORMAL",...]
  const [vipMinScore, setVipMinScore] = useState({});
  const [vipMaxScore, setVipMaxScore] = useState({});
  const [vipHitRatePct, setVipHitRatePct] = useState({}); // UI %
  const [vipLetterBoxPct, setVipLetterBoxPct] = useState({});
  const [vipJackpotStr, setVipJackpotStr] = useState({});

  // Ranking rewards
  const [top1, setTop1] = useState();
  const [top2, setTop2] = useState();
  const [top3, setTop3] = useState();
  const [top4, setTop4] = useState();
  const [top5, setTop5] = useState();
  const [top6, setTop6] = useState();
  const [top7, setTop7] = useState();
  const [top8, setTop8] = useState();
  const [top9, setTop9] = useState();
  const [top10, setTop10] = useState();
  const [preWithdrawRounds, setPreWithdrawRounds] = useState(8);

  const [phraseScores, setPhraseScores] = useState(() =>
    Object.fromEntries(PHRASE_DEFS.map((p) => [p.key, p.defaultScore]))
  );
  const [letterRates, setLetterRates] = useState(() =>
    Object.fromEntries(LETTER_DEFS.map((l) => [l.key, l.defaultRate]))
  );

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    getDataConfig();
  }, []);

  const arrToDashString = (arr) => (Array.isArray(arr) ? arr.join(" - ") : "");

  const dashStringToNumArr = (s) => {
    if (!s?.trim()) return [];
    return s
      .split(/[-,]/g)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => parseInt(x, 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
  };

  const isValidPoints = (s) => {
    if (!s?.trim()) return false;
    const parts = s.split(/[-,]/g).map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return false;
    return parts.every((p) => Number.isFinite(parseInt(p, 10)) && parseInt(p, 10) >= 0);
  };

  const validNonNegativeNumber = (v) => {
    if (v === undefined || v === null || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0;
  };

  const validPositiveNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  };

  const validPercent = (v) => {
    if (v === undefined || v === null || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  };

  const getDataConfig = async () => {
    try {
      const res = await API.get(`${CONFIG}`);
      const data = res?.data?.data ?? {};
      const keys = Object.keys(data.vipLevels ?? {});
      setVipKeys(keys);

      const nextMin = {}, nextMax = {}, nextHitPct = {}, nextBox = {}, nextJpStr = {};
      keys.forEach((k) => {
        const lvl = data.vipLevels[k] ?? {};
        nextMin[k] = Number(lvl.MIN_SCORE ?? 0);
        nextMax[k] = Number(lvl.MAX_SCORE ?? 0);
        nextHitPct[k] = toPercent(lvl.HIT_RATE, 0);
        nextBox[k] = toPercent(lvl.JADE_BOX_RATE, 0);
        nextJpStr[k] = arrToDashString(data.pointsJackpot?.[k] ?? []);
      });
      setVipMinScore(nextMin);
      setVipMaxScore(nextMax);
      setVipHitRatePct(nextHitPct);
      setVipLetterBoxPct(nextBox);
      setVipJackpotStr(nextJpStr);

      setTop1(data?.eventRewards?.TOP1);
      setTop2(data?.eventRewards?.TOP2);
      setTop3(data?.eventRewards?.TOP3);
      setTop4(data?.eventRewards?.TOP4);
      setTop5(data?.eventRewards?.TOP5);
      setTop6(data?.eventRewards?.TOP6);
      setTop7(data?.eventRewards?.TOP7);
      setTop8(data?.eventRewards?.TOP8);
      setTop9(data?.eventRewards?.TOP9);
      setTop10(data?.eventRewards?.TOP10);
      setPreWithdrawRounds(
        Number(
          data?.multiple ??
            data?.preWithdrawRounds ??
            data?.gameplay?.preWithdrawRounds ??
            8
        ) || 8
      );

      const phraseSrc = pickFirstObject(
        data?.wordBonusScores,
        data?.wordRewards,
        data?.combinationRewards,
        data?.phraseScores,
        data?.phraseRewards
      );
      setPhraseScores(
        Object.fromEntries(
          PHRASE_DEFS.map((p) => [
            p.key,
            readAliasedNumber(phraseSrc, [p.key, ...p.aliases], p.defaultScore),
          ])
        )
      );

      const letterSrc = pickFirstObject(
        data?.letterRates,
        data?.charRates,
        data?.letterDropRates,
        data?.characterRates
      );
      setLetterRates(
        Object.fromEntries(
          LETTER_DEFS.map((l) => [
            l.key,
            readAliasedNumber(letterSrc, [l.key, l.key.toLowerCase()], l.defaultRate),
          ])
        )
      );

      setConfigGame({ ...data, pointsJackpot: data.pointsJackpot ?? {} });
    } catch (err) {
      console.log(err);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      setIsShowModalConfirm(false);

      for (const k of vipKeys) {
        const min = vipMinScore[k];
        const max = vipMaxScore[k];
        const hit = vipHitRatePct[k];
        const box = vipLetterBoxPct[k];
        const pts = vipJackpotStr[k];

        if (max < min) {
          showError(`MAX_SCORE của ${k} phải ≥ MIN_SCORE`);
          return;
        }
        if (!validPercent(hit) || !validPercent(box)) {
          showError(`Các tỉ lệ của ${k} phải trong khoảng 0 - 100%`);
          return;
        }
        const overflow = vipRateOverflowError(hit, box);
        if (overflow) {
          showError(`${k}: ${overflow}`);
          return;
        }
        if (!isValidPoints(pts)) {
          showError(`Điểm jackpot của ${k} không hợp lệ.`);
          return;
        }
      }

      if (
        !validPositiveNumber(top1) ||
        !validPositiveNumber(top2) ||
        !validPositiveNumber(top3) ||
        !validPositiveNumber(top4) ||
        !validPositiveNumber(top5) ||
        !validPositiveNumber(top6) ||
        !validPositiveNumber(top7) ||
        !validPositiveNumber(top8) ||
        !validPositiveNumber(top9) ||
        !validPositiveNumber(top10)
      ) {
        showError("Bảng xếp hạng phải là số dương (>0).");
        return;
      }
      if (!validNonNegativeNumber(preWithdrawRounds)) {
        showError("Vòng cược trước khi rút phải ≥ 0.");
        return;
      }

      for (const p of PHRASE_DEFS) {
        const n = Number(phraseScores[p.key]);
        if (!Number.isFinite(n) || n < 0) {
          showError("Điểm thưởng cụm từ phải ≥ 0.");
          return;
        }
      }
      for (const l of LETTER_DEFS) {
        const n = Number(letterRates[l.key]);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          showError("Tỷ lệ nhận chữ phải trong khoảng 0 - 100%.");
          return;
        }
      }
      const letterSumErr = letterRateSumError(letterRates);
      if (letterSumErr) {
        showError(letterSumErr);
        return;
      }

      const vipLevelsBody = {};
      const pointsJackpotBody = {};
      vipKeys.forEach((k) => {
        vipLevelsBody[k] = {
          ...(configGame?.vipLevels?.[k] ?? {}),
          MIN_SCORE: Number(vipMinScore[k] ?? 0),
          MAX_SCORE: Number(vipMaxScore[k] ?? 0),
          HIT_RATE: toDecimalRate(vipHitRatePct[k] ?? 0),
          JADE_BOX_RATE: toDecimalRate(vipLetterBoxPct[k] ?? 0),
        };
        pointsJackpotBody[k] = dashStringToNumArr(vipJackpotStr[k] ?? "");
      });

      const body = {
        config: {
          vipLevels: vipLevelsBody,
          eventRewards: {
            TOP1: Number(top1),
            TOP2: Number(top2),
            TOP3: Number(top3),
            TOP4: Number(top4),
            TOP5: Number(top5),
            TOP6: Number(top6),
            TOP7: Number(top7),
            TOP8: Number(top8),
            TOP9: Number(top9),
            TOP10: Number(top10),
          },
          pointsJackpot: pointsJackpotBody,
          multiple: Number(preWithdrawRounds ?? 0),
        },
      };

      const wordBonusScores = Object.fromEntries(
        PHRASE_DEFS.map((p) => [p.key, Number(phraseScores[p.key] ?? p.defaultScore)])
      );
      const letterRatesBody = Object.fromEntries(
        LETTER_DEFS.map((l) => [l.key, Number(letterRates[l.key] ?? l.defaultRate)])
      );

      await API.patch(`${UPDATE_CONFIG}`, body);
      await API.patch(UPDATE_WORD_BONUS, { wordBonusScores });
      await API.patch(UPDATE_LETTER_RATES, { letterRates: letterRatesBody });
      setIsShowModalSuccess(true);
    } catch (err) {
      console.log(err);
      showError(err?.data?.message || err?.response?.data?.message || "Cập nhật thất bại.");
    }
  };

  const showError = (msg) => messageApi.open({ type: "error", content: msg });
  const handleShowModal = () => {
    for (const k of vipKeys) {
      const overflow = vipRateOverflowError(vipHitRatePct[k], vipLetterBoxPct[k]);
      if (overflow) {
        showError(`${k}: ${overflow}`);
        return;
      }
    }
    const letterSumErr = letterRateSumError(letterRates);
    if (letterSumErr) {
      showError(letterSumErr);
      return;
    }
    setIsShowModalConfirm(true);
  };

  const letterTotal = sumLetterRates(letterRates);
  const letterOver = letterTotal > 100;
  const letterNot100 = Math.abs(letterTotal - 100) > 0.01;
  const letterStatus = letterOver ? "error" : letterNot100 ? "warning" : undefined;

  return (
    <div className="col-12" style={{ background: "#f7f9fa", minHeight: "100vh", padding: 24 }}>
      {contextHolder}
      <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Cài đặt điểm nhận</div>

      <div style={{ background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 2px 8px #eee", marginBottom: 32 }}>
        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Cấp VIP và tỉ lệ trả thưởng</div>
        <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
          <div style={{ width: "100%" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: "12px", marginBottom: 8 }}>
              <div></div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Điểm nhận cao nhất trong 1 lượt</div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Tỉ lệ % điểm thưởng</div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Tỉ lệ % hộp chữ ngọc</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {vipKeys.map((k) => {
                const hit = vipHitRatePct[k];
                const box = vipLetterBoxPct[k];
                const over = sumRates(hit, box) > 100;
                const hint = vipRateSumHint(hit, box);
                const status = over ? "error" : hint ? "warning" : undefined;
                return (
                <div key={k}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: "12px", alignItems: "center" }}>
                  <span style={{ textAlign: "right" }}>{k}:</span>
                  <InputNumber style={{ width: "100%" }} value={vipMaxScore[k]} onChange={(v) => setVipMaxScore((p) => ({ ...p, [k]: Number(v ?? 0) }))} min={0} />
                  <InputNumber style={{ width: "100%" }} status={status} value={hit} onChange={(v) => setVipHitRatePct((p) => ({ ...p, [k]: Number(v ?? 0) }))} min={0} max={100} step={0.01} addonAfter="%" />
                  <InputNumber style={{ width: "100%" }} status={status} value={box} onChange={(v) => setVipLetterBoxPct((p) => ({ ...p, [k]: Number(v ?? 0) }))} min={0} max={100} step={0.01} addonAfter="%" />
                </div>
                {hint && (
                  <div style={{ color: over ? "#ff4d4f" : "#d48806", fontSize: 12, marginLeft: 152, marginTop: 4 }}>
                    {hint}
                  </div>
                )}
                </div>
              );
              })}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center" style={{ marginBottom: 24 }}>
          <div className="flex w-[20%] justify-end mr-10">Vòng cược trước khi rút:</div>
          <div className="w-[60%]">
            <InputNumber style={{ width: "100%" }} value={preWithdrawRounds} onChange={(v) => setPreWithdrawRounds(Number(v ?? 0))} min={0} max={999} />
          </div>
        </div>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Bảng xếp hạng</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {[{ v: top1, set: setTop1, l: "Top 1" }, { v: top2, set: setTop2, l: "Top 2" }, { v: top3, set: setTop3, l: "Top 3" }, { v: top4, set: setTop4, l: "Top 4" }, { v: top5, set: setTop5, l: "Top 5" }, { v: top6, set: setTop6, l: "Top 6" }, { v: top7, set: setTop7, l: "Top 7" }, { v: top8, set: setTop8, l: "Top 8" }, { v: top9, set: setTop9, l: "Top 9" }, { v: top10, set: setTop10, l: "Top 10" }].map((o, i) => (
            <div key={i} className="flex w-full items-center">
              <div className="flex w-[20%] justify-end mr-10">{o.l}:</div>
              <div className="w-[60%]">
                <InputNumber style={{ width: "100%" }} value={o.v} onChange={o.set} min={1} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Số điểm nhận chị Hằng</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {vipKeys.map((k) => (
            <div key={k} style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: "12px", alignItems: "center" }}>
              <span style={{ textAlign: "right" }}>{k}:</span>
              <Input
                style={{ width: "100%" }}
                value={vipJackpotStr[k] ?? ""}
                placeholder="Ví dụ: 8 - 28 - 38 - 58"
                onChange={(e) => setVipJackpotStr((p) => ({ ...p, [k]: e.target.value }))}
                onBlur={(e) => {
                  const arr = dashStringToNumArr(e.target.value);
                  setVipJackpotStr((p) => ({ ...p, [k]: arrToDashString(arr) }));
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Số điểm nhận của cụm từ</div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
          <thead>
            <tr>
              <th style={tableHeader}>Bộ chữ</th>
              <th style={tableHeader}>Điểm thưởng</th>
            </tr>
          </thead>
          <tbody>
            {PHRASE_DEFS.map((p) => (
              <tr key={p.key}>
                <td style={{ ...tableCell, fontWeight: 600 }}>{p.label}</td>
                <td style={tableCell}>
                  <InputNumber
                    style={{ width: 180 }}
                    min={0}
                    value={phraseScores[p.key]}
                    onChange={(v) => setPhraseScores((s) => ({ ...s, [p.key]: Number(v ?? 0) }))}
                    addonAfter="điểm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Cấu hình tỷ lệ nhận chữ</div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: letterNot100 ? 8 : 24 }}>
          <thead>
            <tr>
              <th style={{ ...tableHeader, width: 80 }}>STT</th>
              <th style={tableHeader}>Chữ cái</th>
              <th style={tableHeader}>Tỷ lệ nhận</th>
            </tr>
          </thead>
          <tbody>
            {LETTER_DEFS.map((l, idx) => (
              <tr key={l.key}>
                <td style={tableCell}>{idx + 1}</td>
                <td style={{ ...tableCell, fontWeight: 600 }}>{l.key}</td>
                <td style={tableCell}>
                  <InputNumber
                    style={{ width: 160 }}
                    status={letterStatus}
                    min={0}
                    max={100}
                    step={0.1}
                    precision={2}
                    value={letterRates[l.key]}
                    onChange={(v) => setLetterRates((s) => ({ ...s, [l.key]: Number(v ?? 0) }))}
                    addonAfter="%"
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tableCell, fontWeight: 600 }} colSpan={2}>Tổng</td>
              <td style={{ ...tableCell, fontWeight: 600, color: letterOver ? "#ff4d4f" : letterNot100 ? "#d48806" : undefined }}>
                {letterTotal}%
              </td>
            </tr>
          </tbody>
        </table>
        {letterNot100 && (
          <div style={{ color: letterOver ? "#ff4d4f" : "#d48806", fontSize: 12, marginBottom: 24 }}>
            {letterOver
              ? `Tổng tỷ lệ nhận chữ ${letterTotal}% vượt 100%.`
              : `Tổng tỷ lệ nhận chữ phải bằng 100% (hiện ${letterTotal}%).`}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button type="primary" size="large" style={{ minWidth: 120 }} onClick={handleShowModal}>
            Cập nhật
          </Button>
        </div>
      </div>

      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}

      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => setIsShowModalSuccess(false)}
          close={() => setIsShowModalSuccess(false)}
        />
      )}
    </div>
  );
};

export default SetupScore;
